'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import Footer from '@/components/Footer'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error)
  }, [error])

  return (
    <div className="min-h-screen bg-bg-primary relative flex flex-col">
      <div className="flex-1 flex items-center justify-center pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6 sm:px-8 text-center">

          {/* Error indicator */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-6 mb-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-text-primary leading-tight">
              Something went wrong
            </h1>
            <p className="text-base sm:text-lg text-text-secondary leading-relaxed max-w-xl mx-auto">
              An unexpected error occurred. Most system failures are predictable — but not all. Let's get you back on track.
            </p>
            {process.env.NODE_ENV === 'development' && error.message && (
              <div className="mt-6 p-4 bg-bg-elevated border border-red-500/30 rounded-sm text-left">
                <p className="text-xs font-mono text-red-400 mb-2">Error details (dev only):</p>
                <p className="text-xs text-text-muted font-mono break-all">{error.message}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={reset}
              className="inline-block px-6 py-2.5 bg-accent-primary/10 border border-accent-primary/30 text-accent-primary rounded-sm hover:bg-accent-primary/20 hover:border-accent-primary/50 transition-all duration-150 text-sm font-mono font-light"
            >
              Try again
            </button>
            <Link
              href="/"
              className="inline-block px-6 py-2.5 bg-bg-elevated border border-border-subtle text-text-primary rounded-sm hover:bg-bg-secondary hover:border-accent-primary/30 transition-all duration-150 text-sm font-light"
            >
              Back to home
            </Link>
          </div>

          {/* Support */}
          <div className="mt-12 pt-8 border-t border-border-subtle">
            <p className="text-sm text-text-muted leading-relaxed">
              If this keeps happening,{' '}
              <Link href="/contact" className="text-accent-primary hover:text-accent-primary/80 transition-colors underline">
                let me know
              </Link>
              .
            </p>
          </div>

        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  )
}
